import React, { useState } from 'react';
import { JsonInputForm, useWebhook, ContactForm, EventTracker } from '@your-org/make-webhook-client';

const WebhookExample = () => {
  const [webhookResponse, setWebhookResponse] = useState(null);
  const [error, setError] = useState(null);

  // useWebhook 훅 사용
  const {
    sendAnalyticsEvent,
    sendContactEvent,
    sendBatchEvents,
    sendCustomEvent,
    isLoading,
    error: webhookError,
    lastResponse,
    reset
  } = useWebhook({
    onSuccess: (response) => {
      console.log('Webhook 성공:', response);
      setWebhookResponse(response);
      setError(null);
    },
    onError: (error) => {
      console.error('Webhook 오류:', error);
      setError(error.message);
      setWebhookResponse(null);
    }
  });

  // 분석 이벤트 전송 예시
  const handleAnalyticsEvent = async () => {
    try {
      const response = await sendAnalyticsEvent({
        event: 'button_click',
        data: {
          page: 'webhook_example',
          button: 'analytics_test',
          category: 'test'
        }
      });
      console.log('분석 이벤트 전송 성공:', response);
    } catch (err) {
      console.error('분석 이벤트 전송 실패:', err);
    }
  };

  // 배치 이벤트 전송 예시
  const handleBatchEvent = async () => {
    try {
      const response = await sendBatchEvents({
        events: [
          {
            event: 'page_view',
            data: { page: 'webhook_example' }
          },
          {
            event: 'user_action',
            data: { action: 'test_batch' }
          }
        ],
        batchId: 'test-batch-' + Date.now()
      });
      console.log('배치 이벤트 전송 성공:', response);
    } catch (err) {
      console.error('배치 이벤트 전송 실패:', err);
    }
  };

  // 커스텀 이벤트 전송 예시
  const handleCustomEvent = async () => {
    try {
      const response = await sendCustomEvent(
        'custom_test_event',
        { customData: 'test_value', timestamp: new Date().toISOString() },
        'analytics'
      );
      console.log('커스텀 이벤트 전송 성공:', response);
    } catch (err) {
      console.error('커스텀 이벤트 전송 실패:', err);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Webhook 패키지 사용 예시</h1>
      
      {/* 상태 표시 */}
      <div className="mb-6 p-4 bg-gray-100 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">현재 상태</h2>
        <p>로딩 상태: {isLoading ? '로딩 중...' : '대기 중'}</p>
        {webhookError && <p className="text-red-600">오류: {webhookError.message}</p>}
        {lastResponse && (
          <div className="mt-2">
            <p className="text-green-600">마지막 응답:</p>
            <pre className="text-sm bg-white p-2 rounded">{JSON.stringify(lastResponse, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* 테스트 버튼들 */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">기능 테스트</h2>
        <div className="space-y-2">
          <button
            onClick={handleAnalyticsEvent}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            분석 이벤트 전송
          </button>
          
          <button
            onClick={handleBatchEvent}
            disabled={isLoading}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 ml-2"
          >
            배치 이벤트 전송
          </button>
          
          <button
            onClick={handleCustomEvent}
            disabled={isLoading}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 ml-2"
          >
            커스텀 이벤트 전송
          </button>
          
          <button
            onClick={reset}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 ml-2"
          >
            상태 초기화
          </button>
        </div>
      </div>

      {/* JsonInputForm 컴포넌트 */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">JSON 입력 폼</h2>
        <JsonInputForm
          webhookType="analytics"
          eventName="custom_json_event"
          onSubmit={(data) => console.log('JSON 폼 제출:', data)}
          onSuccess={(response) => console.log('JSON 폼 성공:', response)}
          onError={(error) => console.error('JSON 폼 오류:', error)}
          className="border rounded-lg p-4"
          submitButtonText="JSON 이벤트 전송"
          placeholder='{"event": "test", "data": {"key": "value"}}'
        />
      </div>

      {/* ContactForm 컴포넌트 */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">연락처 폼</h2>
        <ContactForm
          onSubmit={(data) => console.log('연락처 폼 제출:', data)}
          onSuccess={(response) => console.log('연락처 폼 성공:', response)}
          onError={(error) => console.error('연락처 폼 오류:', error)}
          className="border rounded-lg p-4"
          submitButtonText="연락처 정보 전송"
        />
      </div>

      {/* EventTracker 컴포넌트 */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">이벤트 추적기</h2>
        <EventTracker
          trackPageViews={true}
          trackButtonClicks={true}
          customEvents={[
            {
              name: 'test_custom',
              selector: '.test-button',
              event: 'click'
            }
          ]}
          onEventTracked={(eventData) => console.log('이벤트 추적됨:', eventData)}
        />
        <button className="test-button px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600">
          추적 테스트 버튼
        </button>
      </div>

      {/* 응답 표시 */}
      {webhookResponse && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800 mb-2">성공 응답</h3>
          <pre className="text-sm text-green-700 bg-white p-2 rounded overflow-auto">
            {JSON.stringify(webhookResponse, null, 2)}
          </pre>
        </div>
      )}

      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800 mb-2">오류</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default WebhookExample; 